import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const AboutHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const AboutTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const AboutSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
`;

const AboutSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 60px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    padding: 40px 30px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 30px;
  text-align: center;
`;

const AboutText = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #374151;
  
  p {
    margin-bottom: 24px;
  }
  
  strong {
    color: #2563eb;
    font-weight: 600;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  padding: 40px;
  border-radius: 12px;
  margin: 40px 0;
  text-align: center;
`;

const HighlightText = styled.p`
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
`;

const MissionBox = styled.div`
  background: #f8fafc;
  border-left: 4px solid #2563eb;
  padding: 30px;
  border-radius: 8px;
  margin: 30px 0;
`;

const MissionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 15px;
`;

const MissionText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #374151;
  margin: 0;
`;

const About = () => {
  return (
    <AboutContainer>
      <AboutContent>
        <AboutHeader>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <AboutTitle>About Us</AboutTitle>
            <AboutSubtitle>
              Discover how M²eBooks is revolutionizing multimedia publishing
            </AboutSubtitle>
          </motion.div>
        </AboutHeader>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <AboutSection>
            <AboutText>
              <p>
                <strong>M²eBooks</strong> is a multimedia publishing platform transforming how stories are experienced. We go beyond traditional eBooks by blending each chapter with character visuals, videos, chapter recaps and interactive features that bring plots and people to life.
              </p>

              <p>
                Through our exclusive partnership with <strong><a href="https://manhattanhanovertalent.com" target="_blank" rel="noopener noreferrer" style={{color: '#2563eb', textDecoration: 'none'}}>Manhattan Hanover Talent</a></strong>, we link authors' characters to real people—individuals we call <strong>Talent</strong>—with families, friends, and fan bases of their own. This unique connection helps every story reach farther, as character talent actively promotes the books they appear in. Readers can like, share, and engage with visuals and videos, making the story world feel alive and connected.
              </p>

              <HighlightBox>
                <HighlightText>
                  "We believe reading should be more than turning pages—it should be immersive, social, and unforgettable."
                </HighlightText>
              </HighlightBox>

              <p>
                That's why <strong>M²eBooks</strong> fuses storytelling artistry with media innovation, talent-powered marketing, and interactive features, creating eBooks that are as marketable as they are memorable.
              </p>

              <MissionBox>
                <MissionTitle>Our Mission</MissionTitle>
                <MissionText>
                  Empower authors with powerful tools to share their stories, give readers richer ways to enjoy them, and offer talent new opportunities to amplify their presence.
                </MissionText>
              </MissionBox>

              <p>
                <strong>M²eBooks</strong> is where books meet innovation—stories that come alive in your hands.
              </p>
            </AboutText>
          </AboutSection>
        </motion.div>
      </AboutContent>
    </AboutContainer>
  );
};

export default About;
